import Config from "../../../../../../../config"
import styles from "../../styles.module.scss"
import { observer } from "mobx-react-lite"
import {
  deleteAgentProfile,
  upsertAgentProfile,
} from "../../../../../../../services/firebase-functions/agentProfile"
import { useStores } from "../../../../../../../models/helpers/useStores"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { colors } from "@theme/colors"
import FormTextArea from "../../../../../../atoms/FormTextArea"
import { useAgentLink } from "../../useAgentLink"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"
import { isPhoneValid } from "../../../../../../../utils/validation"
import { addTestCollaboratorToTrip } from "../../../../../../../services/firestore/developerFunctions"
import { useEffect, useMemo, useRef } from "react"
import LinkOutline from "~icons/eva/link-2-outline"
import EditFilledIcon from "../../../../../../assets/icons/EditFilledIcon"
import UserAvatar from "../../../../../../atoms/UserAvatar"
import { makeAutoObservable } from "mobx"
import FormTextInput from "../../../../../../atoms/FormTextInput"
import { FormPhoneInput } from "../../../../../../atoms/FormPhoneInput"
import { FormSelectCreateableInput } from "../../../../../../atoms/FormSelectCreateableInput"
import {
  getAgencyConsortia,
  upsertAgencyConsortia,
  upsertHostAgency,
} from "../../../../../../../services/firebase-functions/dictionaries"
import { getHostAgencies } from "../../../../../../../services/firebase-functions/dictionaries"
import { signOut } from "../../../../../../../services/auth"
import { updateUser, updateUserDataInTrips } from "../../../../../../../services/firestore/user"
import { getAvatarDownloadUrl } from "../../../../../../../services/firestore/userAvatar"
import { uploadAvatar } from "../../../../../../../services/firestore/userAvatar"
import { IUser } from "../../../../../../../../../firebase/functions/src/libs/cruisen-contract/user/user.interface"
import { getAuth } from "firebase/auth"
import { AccountModalFooter } from "../../components/Footer"
import { DefaultSpinner } from "../../../../../../loaders/DefaultSpinner"

type AccountModalFormValues = {
  firstName: string
  lastName: string
  phoneNumber: string
  email: string
  shortBio: string
  favoriteTravelDestination: string
  bestTravelAdvice: string
  agencyName: string
  agencyPhoneNumber: string
  hostAgencyId: string
  consortiaId: string
}

const accountModalSchema = yup.object().shape({
  firstName: yup.string().required("-"),
  lastName: yup.string().required("-"),
  phoneNumber: yup.string().required("-"),
  email: yup.string().required("-"),
  shortBio: yup.string(),
  favoriteTravelDestination: yup.string(),
  bestTravelAdvice: yup.string(),
  agencyName: yup.string(),
  agencyPhoneNumber: yup.string(),
  hostAgencyId: yup.string(),
  consortiaId: yup.string(),
})

type AccountScreenStoreProperties = {
  isModalWarningVisible: boolean
  isSavingDetails: boolean
  imagePickerResponse: File | null
  newAvatarURL: string | null
}

class MyProfileTabStore {
  isModalWarningVisible = false
  isSavingDetails = false
  imagePickerResponse = null
  newAvatarURL = null

  constructor() {
    makeAutoObservable(this)
  }

  setProperty<K extends keyof AccountScreenStoreProperties>(
    key: K,
    value: AccountScreenStoreProperties[K],
  ): void {
    ;(this as AccountScreenStoreProperties)[key] = value
  }
}

export const MyProfileTab = observer(
  ({
    openSubscriptionModal,
    closeModal,
    setIsModalWarningVisible,
    setIsContentChanged,
    logout,
    isLoggingOut,
  }: {
    openSubscriptionModal: () => void
    closeModal: () => void
    setIsModalWarningVisible: (value: boolean) => void
    setIsContentChanged: (value: boolean) => void
    logout: () => void
    isLoggingOut: boolean
  }) => {
    const rootStore = useStores()
    const avatarInputRef = useRef<HTMLInputElement | null>(null)
    const store = useMemo(() => new MyProfileTabStore(), [])
    const { loggedInUser, agentProfile } = rootStore
    const currentUser = getAuth().currentUser

    const { data: hostAgencies, isLoading: isLoadingHostAgencies } = useQuery({
      queryKey: ["hostAgencies"],
      queryFn: () => getHostAgencies(),
    })

    const { data: consortia, isLoading: isLoadingConsortia } = useQuery({
      queryKey: ["consortia"],
      queryFn: () => getAgencyConsortia(),
    })

    const { copyLink, isLinkCopied, shareLink } = useAgentLink(agentProfile?.agentLink || "")

    const { mutate: mutateDeleteAgentProfile, isPending: isDeletingAgentProfile } = useMutation({
      mutationFn: () => deleteAgentProfile(),
      onSuccess: () => {
        signOut()
      },
    })

    const queryClient = useQueryClient()

    const handleUpdateAccount = async (formValues: AccountModalFormValues) => {
      store.setProperty("isSavingDetails", true)

      const updatedUserData: Partial<IUser> = {
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        phoneNumber: formValues.phoneNumber,
        email: formValues.email,
      }

      try {
        // Upload avatar file to firebase storage
        if (store.imagePickerResponse) {
          await uploadAvatar(currentUser.uid, store.imagePickerResponse)
          const url = await getAvatarDownloadUrl(currentUser.uid)
          updatedUserData.avatar = url
        }

        await updateUserDataInTrips({
          userId: loggedInUser.id,
          updatedData: {
            firstName: formValues.firstName,
            lastName: formValues.lastName,
            avatar: updatedUserData?.avatar || null,
          },
        })

        await updateUser(loggedInUser.id, updatedUserData)

        const updatedAgentProfile = await upsertAgentProfile({
          agentProfile: {
            id: agentProfile.id,
            userId: loggedInUser.id,
            phoneNumber: updatedUserData.phoneNumber,
            email: updatedUserData.email,
            shortBio: formValues.shortBio,
            favouriteTravelDestination: formValues.favoriteTravelDestination,
            bestTravelAdvice: formValues.bestTravelAdvice,
            agencyName: formValues.agencyName,
            agencyPhoneNumber: isAgencyPhoneNumberValid ? formValues.agencyPhoneNumber : "",
            hostAgencyId: Number(formValues.hostAgencyId) || null,
            consortiaId: Number(formValues.consortiaId) || null,
          },
        })

        if (updatedAgentProfile) {
          rootStore.setProp("agentProfile", updatedAgentProfile)
        }

        // Clean up the new avatar URL
        if (store.newAvatarURL) {
          URL.revokeObjectURL(store.newAvatarURL)
          store.setProperty("newAvatarURL", null)
        }

        if (store.imagePickerResponse) {
          store.setProperty("imagePickerResponse", null)
        }

        rootStore.setProp("loggedInUser", {
          ...loggedInUser,
          ...updatedUserData,
        })
      } catch (error) {
        console.error(error)
      } finally {
        store.setProperty("isSavingDetails", false)
        closeModal()
      }
    }

    const createHostAgencyOptionMutation = useMutation({
      mutationFn: async (hostAgencyName: string) => {
        const newHostAgency = await upsertHostAgency({ hostAgencyName })

        queryClient
          .invalidateQueries({ queryKey: ["hostAgencies"] })
          .catch((error) => console.error("Error invalidating host agencies query", error))

        return {
          value: String(newHostAgency.id),
          label: newHostAgency.hostAgencyName,
        }
      },
    })

    const createConsortiaOptionMutation = useMutation({
      mutationFn: async (consortiaName: string) => {
        const newConsortia = await upsertAgencyConsortia({ consortiaName })

        queryClient
          .invalidateQueries({ queryKey: ["consortia"] })
          .catch((error) => console.error("Error invalidating consortia query", error))

        return {
          value: String(newConsortia.id),
          label: newConsortia.consortiaName,
        }
      },
    })

    const {
      control,
      reset,
      handleSubmit,
      watch,
      formState: { isValid, isDirty },
    } = useForm({
      defaultValues: {
        firstName: loggedInUser?.firstName,
        lastName: loggedInUser?.lastName,
        email: loggedInUser?.email,
        phoneNumber: loggedInUser?.phoneNumber || "",
        shortBio: agentProfile?.shortBio,
        favoriteTravelDestination: agentProfile?.favouriteTravelDestination,
        bestTravelAdvice: agentProfile?.bestTravelAdvice,
        agencyName: agentProfile?.agencyName,
        agencyPhoneNumber: agentProfile?.agencyPhoneNumber || "",
        hostAgencyId: String(agentProfile?.hostAgency?.id),
        consortiaId: String(agentProfile?.consortia?.id),
      },
      resolver: yupResolver(accountModalSchema),
      mode: "all",
    })

    const [phoneNumber, agencyPhoneNumber] = watch(["phoneNumber", "agencyPhoneNumber"])
    const isPhoneNumberValid = isPhoneValid(phoneNumber)
    const isAgencyPhoneNumberValid = isPhoneValid(agencyPhoneNumber)

    const sortedHostAgencies =
      hostAgencies
        ?.map((hostAgency) => ({
          value: String(hostAgency.id),
          label: hostAgency.hostAgencyName,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)) || []

    const sortedConsortia =
      consortia
        ?.map((consortia) => ({
          value: String(consortia.id),
          label: consortia.consortiaName,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)) || []

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        const selectedFile = event.target.files[0]

        // Clean up the previous avatar URL
        if (store.newAvatarURL) {
          URL.revokeObjectURL(store.newAvatarURL)
        }

        store.setProperty("imagePickerResponse", selectedFile)
        store.setProperty("newAvatarURL", URL.createObjectURL(selectedFile))
      }
    }

    const isChanged = isDirty || store.newAvatarURL

    useEffect(() => {
      setIsContentChanged(isChanged)
    }, [isChanged])

    const handleCloseModal = () => {
      if (isChanged) {
        setIsModalWarningVisible(true)
        return
      }

      if (store.newAvatarURL) {
        URL.revokeObjectURL(store.newAvatarURL)
      }
      closeModal()
    }

    const isCancelDisabled = isLoggingOut || store.isModalWarningVisible || store.isSavingDetails
    const isSaveDisabled =
      !isValid ||
      isLoggingOut ||
      !isChanged ||
      store.isSavingDetails ||
      store.isModalWarningVisible ||
      !isPhoneNumberValid ||
      (agencyPhoneNumber && agencyPhoneNumber !== "+1" && !isAgencyPhoneNumberValid)

    // Reset the form values when the agent profile is loaded
    useEffect(() => {
      if (agentProfile) {
        reset(
          {
            firstName: loggedInUser?.firstName,
            lastName: loggedInUser?.lastName,
            phoneNumber: loggedInUser?.phoneNumber || "",
            email: loggedInUser?.email,
            shortBio: agentProfile?.shortBio,
            favoriteTravelDestination: agentProfile?.favouriteTravelDestination,
            bestTravelAdvice: agentProfile?.bestTravelAdvice,
            agencyName: agentProfile?.agencyName,
            agencyPhoneNumber: agentProfile?.agencyPhoneNumber || "",
            hostAgencyId: String(agentProfile?.hostAgency?.id),
            consortiaId: String(agentProfile?.consortia?.id),
          },
          { keepDirty: false },
        )
      }
    }, [agentProfile, reset, loggedInUser])

    return (
      <>
        {isLoggingOut ? (
          <div className={styles.loadingContainer}>
            <DefaultSpinner />
          </div>
        ) : (
          <div className={styles.accountModalBody}>
            <input
              type="file"
              ref={avatarInputRef}
              style={{ display: "none" }}
              onChange={(e) => handleFileChange(e)}
              accept=".jpg,.jpeg,.png"
            />
            <div className={styles.emptyDivider} />
            {Config.environment === "development" ? (
              <div className={styles.sectionInfo}>
                <p className={styles.sectionTitle}>Dev actions</p>
                <div className={styles.devButtonsContainer}>
                  <button
                    className={styles.devButton}
                    onClick={() => mutateDeleteAgentProfile()}
                    data-testid="delete-agent-profile-button"
                  >
                    {isDeletingAgentProfile ? "Deleting..." : "Delete agent profile"}
                  </button>
                  <button className={styles.devButton} onClick={openSubscriptionModal}>
                    Show subscription modal
                  </button>
                  <button
                    className={styles.devButton}
                    data-testid="add-test-collaborator-button"
                    onClick={async () => {
                      await addTestCollaboratorToTrip({
                        trip: rootStore.activeTrip,
                      })
                    }}
                  >
                    Add test collaborator
                  </button>
                </div>
              </div>
            ) : null}
            {agentProfile?.agentLink && (
              <>
                <div className={styles.accountLinkContainer}>
                  <div className={styles.sectionInfo}>
                    <p className={styles.sectionTitle}>Your link</p>
                    <p className={styles.sectionDescription}>Invite clients to create new trips.</p>
                  </div>
                  <div className={styles.shareLinkContainer}>
                    <div className={styles.shareLinkWrapper}>
                      <p className={styles.shareLink}>{shareLink}</p>
                    </div>
                    <button className={styles.copyLinkButton} onClick={copyLink}>
                      <LinkOutline className={styles.copyButtonIcon} />
                      <p className={styles.copyButtonText}>
                        {isLinkCopied ? "Copied" : "Copy link"}
                      </p>
                    </button>
                  </div>
                </div>
                <div className={styles.divider} />
              </>
            )}
            <div className={styles.personalInfoContainer}>
              <div className={styles.sectionInfo}>
                <p className={styles.sectionTitle}>Personal info</p>
                <p className={styles.sectionDescription}>Manage your personal details</p>
              </div>
              <div className={styles.personalInfoForm}>
                <div
                  className={styles.avatarContainer}
                  onClick={() => {
                    if (avatarInputRef.current) {
                      avatarInputRef.current.click()
                    }
                  }}
                >
                  <UserAvatar
                    avatar={store.newAvatarURL || loggedInUser.avatar}
                    customSize={130}
                    fontSize="40px"
                    nameInitals={`${loggedInUser.firstName[0]}${loggedInUser.lastName[0]}`}
                  />
                  <div className={styles.avatarEditButton}>
                    <div className={styles.avatarEditContainer}>
                      <EditFilledIcon />
                    </div>
                  </div>
                </div>
                <FormTextInput
                  control={control}
                  name="firstName"
                  label="First name"
                  parentBackgroundColor={colors.backgroundSecondary}
                />
                <FormTextInput
                  control={control}
                  name="lastName"
                  label="Last name"
                  parentBackgroundColor={colors.backgroundSecondary}
                />
                <FormPhoneInput
                  control={control}
                  name="phoneNumber"
                  label="Phone number"
                  parentBackgroundColor={colors.backgroundSecondary}
                />
                <FormTextInput
                  control={control}
                  name="email"
                  label="Email"
                  readOnly
                  disabled
                  parentBackgroundColor={colors.backgroundTertiary}
                />
                <FormTextInput
                  control={control}
                  name="favoriteTravelDestination"
                  label="Favorite travel destination"
                  parentBackgroundColor={colors.backgroundSecondary}
                />
                <FormTextArea
                  control={control}
                  label="Best travel advice"
                  name="bestTravelAdvice"
                  rows={3}
                  resize={false}
                  parentBackgroundColor={colors.backgroundSecondary}
                />
                <FormTextArea
                  control={control}
                  label="Short bio"
                  name="shortBio"
                  rows={3}
                  resize={false}
                  parentBackgroundColor={colors.backgroundSecondary}
                />
              </div>
            </div>
            <div className={styles.divider} />
            <div className={styles.agencyInfoContainer}>
              <div className={styles.sectionInfo}>
                <p className={styles.sectionTitle}>Agency info</p>
                <p className={styles.sectionDescription}>Manage your agency details</p>
              </div>
              <div className={styles.agencyInfoForm}>
                <FormTextInput
                  control={control}
                  name="agencyName"
                  label="Agency name"
                  parentBackgroundColor={colors.backgroundSecondary}
                />
                <FormPhoneInput
                  control={control}
                  name="agencyPhoneNumber"
                  label="Phone number"
                  parentBackgroundColor={colors.backgroundSecondary}
                />
                <FormSelectCreateableInput
                  control={control}
                  name="hostAgencyId"
                  options={sortedHostAgencies}
                  label="Host agency"
                  createOptionMutation={createHostAgencyOptionMutation}
                  isDisabled={isLoadingHostAgencies}
                  parentBackgroundColor={
                    isLoadingHostAgencies ? colors.backgroundTertiary : colors.backgroundSecondary
                  }
                />
                <FormSelectCreateableInput
                  control={control}
                  name="consortiaId"
                  options={sortedConsortia}
                  label="Consortia"
                  createOptionMutation={createConsortiaOptionMutation}
                  isDisabled={isLoadingConsortia}
                  parentBackgroundColor={
                    isLoadingConsortia ? colors.backgroundTertiary : colors.backgroundSecondary
                  }
                />
              </div>
            </div>
            <div className={styles.shortDivider} />
            <button className={styles.logoutButton} onClick={() => logout()}>
              Log out
            </button>
          </div>
        )}
        <AccountModalFooter
          isPending={store.isSavingDetails}
          isCancelDisabled={isCancelDisabled}
          isSaveDisabled={isSaveDisabled}
          onCancel={() => handleCloseModal()}
          onSave={() => handleSubmit(handleUpdateAccount)()}
        />
      </>
    )
  },
)
